import React from 'react';

import { isChunkLoadError } from '@/components/lazyRoute';
import { Button } from '@/components/ui';
import * as Sentry from '@/sentry';

type ErrorBoundaryProps = {
  children: React.ReactNode;
  // When this value changes (e.g. the route path), the boundary clears its error so
  // navigating away from a crashed page recovers without a manual full reload.
  resetKey?: unknown;
};

type ErrorBoundaryState = {
  error: Error | null;
};

// App-level safety net: a thrown render error is caught here and shown as a fallback
// instead of unmounting the whole React tree (which white-screens the entire app, since
// there was previously no boundary anywhere). Errors are reported to Sentry.
export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    Sentry.captureException(error, {
      extra: { componentStack: info.componentStack },
    });
    // eslint-disable-next-line no-console
    console.error('ErrorBoundary caught an error:', error, info.componentStack);
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps) {
    if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  render() {
    if (this.state.error && isChunkLoadError(this.state.error)) {
      return (
        <div role="alert" className="flex justify-center p-8">
          <div className="flex max-w-error-message flex-col items-center gap-4 text-center">
            <h3 className="m-0 text-2xl font-semibold tracking-title text-ink">This page could not be loaded</h3>
            <p className="m-0 text-sm text-ink-muted">
              The app was probably updated while this tab was open, or the connection dropped. Reload to get the latest
              version.
            </p>
            <Button onClick={() => window.location.reload()}>Reload page</Button>
          </div>
        </div>
      );
    }
    if (this.state.error) {
      return (
        <div role="alert" className="flex justify-center p-8">
          <div className="flex max-w-error-message flex-col items-center gap-4 text-center">
            <h3 className="m-0 text-2xl font-semibold tracking-title text-ink">Something went wrong on this page</h3>
            <p className="m-0 text-sm text-ink-muted">
              The page hit an unexpected error. You can reload to try again, or use the menu to go somewhere else. The
              error has been reported automatically.
            </p>
            <Button onClick={() => window.location.reload()}>Reload page</Button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
